import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../api/client'
import type { CompanyDetail as Detail, FieldValue } from '../../api/types'
import { statePill } from './Companies'
import FieldEditor from './FieldEditor'
import CompanyActivity from './CompanyActivity'
import CompanyDiagnostics from './CompanyDiagnostics'
import CompanyDocuments from './CompanyDocuments'
import WorkspaceTabs from './WorkspaceTabs'
import DetailDrawer from './DetailDrawer'
import CompanyWorkflows from './CompanyWorkflows'
import CompanyAssignment from './CompanyAssignment'
import ReplyDrafts from './ReplyDrafts'
import CompanyMeetings from './CompanyMeetings'
import ExtractionHistory from './ExtractionHistory'
import ActivityHistory from './ActivityHistory'
import FormSessions from './FormSessions'
import CompanyInquiries from './CompanyInquiries'
import EmailReconciliationJobs from './EmailReconciliationJobs'
import CalendarReconciliationJobs from './CalendarReconciliationJobs'

export default function CompanyDetail() {
  const { companyId = '' } = useParams()
  const [d, setD] = useState<Detail | null>(null)
  const [tab, setTab] = useState<'work' | 'review' | 'diagnostics'>('work')
  const [selected, setSelected] = useState<FieldValue | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [decision, setDecision] = useState('mark_approved')
  const [decisionReason, setDecisionReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setD(null)
    setTab('work')
    const refresh = () => api.company(companyId).then((x) => {
      if (live) { setD(x); setSelected(old => x.fields.find(f => f.field_id === old?.field_id) ?? null); setError(null) }
    }).catch(e => { if (live) setError(e.message) })
    refresh()
    const timer = window.setInterval(refresh, 4000)
    return () => { live = false; window.clearInterval(timer) }
  }, [companyId])

  async function act(action: string, payload: Record<string, unknown> = {}) {
    setBusy(true)
    try {
      if (action === 'prepare' || action === 'prepare_and_contact' || action === 'request_callback' || action === 'submit_form') {
        await api.startWorkflow([companyId], action, crypto.randomUUID())
        setToast('Workflow queued. Follow progress and inspect agent outputs below.')
      } else {
        await api.action(companyId, action, payload)
        setToast('Decision saved')
        setDecisionReason('')
      }
      setD(await api.company(companyId)); setError(null)
    } catch (e) { setError(e instanceof Error ? e.message : 'Action failed') }
    finally { setBusy(false) }
  }

  if (!d) return error ? <div className="notice crit" role="alert">{error}</div> : <p className="muted">Loading…</p>

  return (
    <>
      <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
        <Link to="/app/companies">← Companies</Link>
      </p>
      <div className="detail-head">
        <h1 style={{ fontSize: 'var(--step-3)' }}>{d.name}</h1>
        <div className="meta">
          {d.url && (
            <a href={d.url} target="_blank" rel="noopener noreferrer" className="mono">
              {d.domain}
            </a>
          )}
          <span>{statePill(d.state)}</span>
          <span>Analyst {d.assigned_analyst_name ?? 'unassigned'}</span>
        </div>
        <div className="detail-actions">
          <button className="btn small" disabled={busy} onClick={() => act('prepare_and_contact')}>{busy ? 'Working…' : 'Prepare & contact'}</button>
          <details className="more-actions"><summary>More actions</summary><div>
          <button className="btn small secondary" disabled={busy} onClick={() => act('prepare')}>Prepare package</button>
          <button className="btn small secondary" disabled={busy} onClick={() => act('request_callback')}>Request callback</button>
          <button className="btn small secondary" disabled={busy} onClick={() => act('submit_form')}>Submit contact form</button>
          </div></details>

        </div>
        {error && <div className="notice crit" role="alert">{error}</div>}
        <p className="muted" style={{marginTop:'1rem'}}>Preparation: {(d.preparation_status ?? 'pending').replace(/_/g,' ')} · Callback {d.callback_number ?? ''}: {d.callback_ready ? 'ready' : 'not yet primed'}</p>
        {toast && (
          <div className="notice" role="status" style={{ marginTop: '0.5rem' }}>
            {toast}
          </div>
        )}
      </div>

      <div className="workspace-layout">
      <WorkspaceTabs id="company" tabs={[{id:'work',label:'Work'},{id:'review',label:'Review'},{id:'diagnostics',label:'Diagnostics'}]} value={tab} onChange={setTab}/>
      {tab === 'work' && <section id="company-panel-work" role="tabpanel" aria-labelledby="company-tab-work" className="company-tab-content">
        <CompanyActivity key={companyId} companyId={companyId} onReview={() => setTab('review')}/>
      </section>}
      {tab === 'review' && <section id="company-panel-review" role="tabpanel" aria-labelledby="company-tab-review" className="company-tab-content review-workspace">
        <section className="review-section">
          <div className="section-heading"><h2>Findings</h2><span className="muted">{d.fields.filter(field => field.status !== 'open').length} recorded</span></div>
          {d.fit_summary && <p>{d.fit_summary}</p>}
          {!d.fields.some(field => field.status !== 'open') && <p className="muted">No findings yet. Vendor answers will appear after a reply or call.</p>}
          <ul className="finding-list">{d.fields.filter(field => field.status !== 'open').map(field => <li key={field.field_id}><button onClick={() => setSelected(field)}><span><strong>{field.label}</strong><span>{field.value || field.status.replace(/_/g,' ')}</span></span><span aria-hidden="true">›</span></button></li>)}</ul>
          {d.red_flags.filter(flag => !/ProviderError|Validation failed|source_ids|Configure:|Spend cap/i.test(flag)).length > 0 && <div className="review-concerns"><h3>Points to review</h3><ul>{d.red_flags.filter(flag => !/ProviderError|Validation failed|source_ids|Configure:|Spend cap/i.test(flag)).map(flag => <li key={flag}>{flag}</li>)}</ul></div>}
        </section>
        <CompanyDocuments company={d}/>
        <details className="quiet-details"><summary>Prepare a reply or add attachments</summary><ReplyDrafts key={companyId} companyId={companyId} messages={d.thread.filter(message=>message.direction==='inbound')}/></details>
        <form className="review-section review-decision" onSubmit={event => { event.preventDefault(); void act(decision, { reason: decisionReason }) }}>
          <h2>Analyst decision</h2>
          <div className="decision-controls"><select className="select" aria-label="Company decision" value={decision} onChange={event => setDecision(event.target.value)} disabled={busy || d.state === 'opted_out'}><option value="mark_approved">Approve</option><option value="mark_disqualified">Disqualify</option><option value="mark_unreachable">Mark unreachable</option><option value="mark_opted_out">Record opt-out</option></select><input className="input" aria-label="Decision reason" placeholder="Reason for this decision" required maxLength={2000} value={decisionReason} onChange={event => setDecisionReason(event.target.value)} disabled={busy || d.state === 'opted_out'}/><button className="btn small secondary" disabled={busy || !decisionReason.trim() || d.state === 'opted_out'}>Save decision</button></div>
          <p className="muted">Saves your reason and stops further outreach.</p>
        </form>
        <details className="quiet-details"><summary>Meetings and analyst assignment</summary><CompanyAssignment companyId={companyId} assigned={d.assigned_analyst} saved={async()=>setD(await api.company(companyId))}/><CompanyMeetings companyId={companyId}/></details>
        {selected && <DetailDrawer title={selected.label} onClose={() => setSelected(null)}>
          {selected.quote ? <blockquote className="quote">“{selected.quote}”<cite>{d.artifacts.find(artifact=>artifact.artifact_id===selected.artifact_id)?.label ?? 'Recorded source'}</cite></blockquote> : <p className="muted">No source quote was saved for this field.</p>}
          <FieldEditor key={selected.field_id} cid={companyId} field={selected} saved={async()=>{setD(await api.company(companyId));setSelected(null);setToast('Correction saved')}}/>
        </DetailDrawer>}
      </section>}
      {tab === 'diagnostics'  && <section id="company-panel-diagnostics" role="tabpanel" aria-labelledby="company-tab-diagnostics" className="company-tab-content">
        <CompanyDiagnostics key={companyId} companyId={companyId} warnings={[...(d.quality_warnings ?? []), ...d.red_flags.filter(flag => /ProviderError|Validation failed|source_ids|Configure:|Spend cap/i.test(flag))]}/>
        <details className="quiet-details"><summary>Recovery and audit tools</summary>
          <CompanyWorkflows key={companyId} history companyId={companyId} onChanged={async () => { setD(await api.company(companyId)) }}/>
          <EmailReconciliationJobs key={companyId} companyId={companyId} saved={async()=>setD(await api.company(companyId))}/>
          <CalendarReconciliationJobs key={companyId} companyId={companyId} saved={async()=>setD(await api.company(companyId))}/>
          <CompanyInquiries key={companyId} companyId={companyId}/>
          <FormSessions key={companyId} companyId={companyId}/>
          <ExtractionHistory companyId={companyId}/>
          <ActivityHistory companyId={companyId}/>
        </details>
      </section>}
      </div>
    </>
  )
}
